import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, GraduationCap } from "lucide-react";
import { useNoIndex } from "@/lib/useSeo";

export default function VerifyEmail() {
  useNoIndex();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Lien invalide. Demande un nouveau lien depuis tes paramètres.");
      return;
    }
    // Guard against React.StrictMode double-invocation in dev/preview
    if (calledRef.current) return;
    calledRef.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("ok");
      } catch (e) {
        setStatus("error");
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
            <GraduationCap size={22} />
          </div>
          <span className="font-display text-xl font-bold text-slate-900">Student Hub</span>
        </div>

        {status === "loading" && (
          <div className="space-y-4" data-testid="verify-loading">
            <Loader2 size={36} className="animate-spin text-indigo-500 mx-auto" />
            <p className="text-slate-600">Vérification en cours…</p>
          </div>
        )}

        {status === "ok" && (
          <div className="space-y-4" data-testid="verify-success">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Email confirmé</h1>
            <p className="text-slate-600">Ton compte est maintenant sécurisé. Tu peux accéder à toutes les fonctionnalités.</p>
            <Link
              to="/dashboard"
              className="inline-block px-5 py-2.5 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-600"
              data-testid="verify-go-dashboard"
            >
              Aller au tableau de bord
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4" data-testid="verify-error">
            <XCircle size={48} className="text-red-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Lien invalide</h1>
            <p className="text-slate-600">{error}</p>
            <Link
              to="/login"
              className="inline-block px-5 py-2.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800"
            >
              Retour à la connexion
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
